/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import styled from 'styled-components';

import ProductImage from './ProductImage';
import type { CMSTradeVintage } from '~/networking/api/cms/responses/CMSWineVintage';
import Image from 'next/image';
import { defaultBottleImage } from '~/stores/wineConstants';
import IconImage from '~/assets/img/marketing/check-circle.svg';
import { GreenButton } from '../TrackRecord/GreenButton';
import { isMobile } from 'react-device-detect';
import Head from 'next/head';

interface ProductHeaderProps {
    vintage: CMSTradeVintage;
    isAuthenticated?: boolean;
}

const ProductHeader: React.FC<ProductHeaderProps> = ({ vintage, isAuthenticated }) => {
    return (
        <>
            {!isAuthenticated && (
                <section
                    className={
                        'md:flex pt-[24px] pb-[24px] mb-[24px] md:mb-0 md:p-[24px] border-b-[1px] md:border-[1px] border-[#DBDBDB] md:rounded-[20px] gap-[40px]'
                    }
                >
                    <div
                        className={
                            'bg-gray-300 rounded-[8px] md:min-w-[452px] flex items-center justify-center md:min-h-[452px]'
                        }
                    >
                        <Image
                            src={`${vintage.bottleImage?.url || defaultBottleImage}`}
                            width={204}
                            height={450}
                            alt={`${vintage.wineInfo.displayName} ${vintage.lwin11}`}
                        />
                    </div>
                    <div className={'md:w-1/2'}>
                        <h2 className={'text-[24px] font-roslindale mt-[15px]'}>
                            Start Your Wine Collection with {vintage.year} {vintage.wineInfo.displayName}
                        </h2>
                        <ul className={'mt-[30px] pr-[40px]'}>
                            <li className={'flex gap-[13px] justify-start items-start mb-[20px]'}>
                                <Image src={IconImage} width={24} height={24} alt={'Icon'} />
                                <span>Begin your portfolio with a prestigious wine that has a history of growth.</span>
                            </li>
                            <li className={'flex gap-[13px] justify-start items-start mb-[20px]'}>
                                <Image src={IconImage} width={24} height={24} alt={'Icon'} />
                                <span>Enjoy fully managed, secure storage facilities with insurance coverage.</span>
                            </li>
                            <li className={'flex gap-[13px] justify-start items-start mb-[20px]'}>
                                <Image src={IconImage} width={24} height={24} alt={'Icon'} />
                                <span>Get expert advice on when to hold and when to sell.</span>
                            </li>
                        </ul>
                        <div className={'flex justify-start items-start max-w-[100%] md:max-w-[200px] mt-[30px]'}>
                            <GreenButton
                                location={'hero'}
                                title={'Get Started'}
                                width={isMobile ? '100%' : '200px'}
                                height={'56px'}
                            />
                        </div>
                    </div>
                </section>
            )}
            {isAuthenticated && (
                <ProductWrapper>
                    <Head>
                        {vintage.wineInfo.producers?.background?.url && (
                            <link rel={'preload'} href={vintage.wineInfo.producers?.background?.url} as={'image'} />
                        )}
                    </Head>
                    <ProductContainer background={vintage.wineInfo.producers?.background?.url}>
                        <ProductImage
                            productImageUrl={vintage.bottleImage}
                            displayName={vintage.wineInfo.displayName}
                            vintage={vintage.lwin11}
                        />
                    </ProductContainer>
                </ProductWrapper>
            )}
        </>
    );
};

export default ProductHeader;

interface ProductContainerProps {
    background?: string;
}
const ProductWrapper = styled.div`
    background-color: rgb(196 196 196 / 20%);
    border-radius: 8px;
    padding: 10px 0 50px 0;
    z-index: 0;
`;
const ProductContainer = styled.div<ProductContainerProps>`
    margin: 0 auto;
    height: 457px;
    width: 800px;
    background-image: url(${(props) => props.background});
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    text-align: center;
    position: relative;
    margin-top: 100px;
    z-index: 5;
    @media screen and (min-width: 768px) and (max-width: 1024px) {
        width: 100%;
    }
    @media screen and (max-width: 767px) {
        width: auto;
        height: 226px;
        display: flex;
        justify-content: center;
    }
`;
